import { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Q1: the quality-permission editor for one user (Settings > users).
 *
 * Different machinery from the module-permission ticks above it, on purpose.
 * A tick decides what a user SEES; these decide what they may DO, and every
 * change here is an act of governance - so an override cannot be saved
 * without its reason, carries an optional end date, and the four submission
 * permissions cannot be denied at all. The server enforces all of this
 * again; this panel just refuses to pretend otherwise.
 */

interface CatalogEntry {
    key: string;
    label: string;
    floor: boolean;
    /** 8 Aug: the catalog became the full matrix; entries carry their module. */
    module?: string;
    /** false = documented, but its route still checks the role directly. */
    enforced?: boolean;
    note?: string | null;
}

interface Override {
    permission: string;
    effect: "grant" | "deny";
    reason: string;
    startsAt: string;
    endsAt: string | null;
    grantedByName: string | null;
}

interface UserPermissions {
    role: string;
    defaults: string[];
    overrides: Override[];
    resolved: Record<string, boolean>;
}

interface PanelProps {
    userId: string;
    userName: string;
}

interface EditorState {
    key: string;
    effect: "grant" | "deny";
    reason: string;
    endsAt: string;
}

const formatDay = (iso: string): string => new Date(iso).toLocaleDateString();

export const QualityPermissionsPanel = ({ userId, userName }: PanelProps) => {
    const [catalog, setCatalog] = useState<CatalogEntry[] | null>(null);
    const [state, setState] = useState<UserPermissions | null>(null);
    const [editor, setEditor] = useState<EditorState | null>(null);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<string | null>(null);

    const load = () =>
        api.get<UserPermissions>(`/quality-permissions/users/${userId}`)
            .then(setState)
            .catch(() => setMessage("Could not load quality permissions"));

    useEffect(() => {
        setState(null);
        setEditor(null);
        setMessage(null);
        api.get<{ permissions: CatalogEntry[] }>("/quality-permissions/catalog")
            .then((c) => setCatalog(c.permissions))
            .catch(() => setMessage("Could not load quality permissions"));
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const overrideFor = (key: string): Override | undefined =>
        state?.overrides.find((o) => o.permission === key);

    const save = async () => {
        if (!editor) return;
        setBusy(true);
        setMessage(null);
        try {
            await api.put(`/quality-permissions/users/${userId}/${editor.key}`, {
                effect: editor.effect,
                reason: editor.reason,
                endsAt: editor.endsAt ? new Date(editor.endsAt).toISOString() : null,
            });
            setEditor(null);
            await load();
        } catch (err) {
            setMessage(err instanceof Error ? err.message : "Could not save the override");
        } finally {
            setBusy(false);
        }
    };

    const clear = async (key: string) => {
        setBusy(true);
        setMessage(null);
        try {
            await api.delete(`/quality-permissions/users/${userId}/${key}`);
            await load();
        } catch (err) {
            setMessage(err instanceof Error ? err.message : "Could not remove the override");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="card">
            <div className="card-head">
                <div>
                    <h2>Quality permissions — {userName}</h2>
                    <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.2rem" }}>
                        What this person may DO in the quality registers. The role sets the
                        defaults; an override changes one thing, says why, and can carry an end
                        date — &ldquo;investigates until 30 Sep because the clinical lead is on
                        leave&rdquo;. Submitting reports can never be taken away.
                    </p>
                </div>
            </div>
            <div className="card-body">
                {!catalog || !state ? (
                    <p style={{ color: "var(--muted)" }}>{message ?? "Loading…"}</p>
                ) : (
                    <div style={{ display: "grid", gap: "0.45rem" }}>
                        {catalog.map((p, i) => {
                            const moduleHeading = p.module && p.module !== catalog[i - 1]?.module ? p.module : null;
                            const override = overrideFor(p.key);
                            const allowed = state.role === "admin" || state.resolved[p.key] === true;
                            const byDefault = state.defaults.includes(p.key);
                            return (
                                <div key={p.key} style={{ display: "flex", gap: "0.6rem", alignItems: "baseline",
                                    flexWrap: "wrap", padding: "0.35rem 0", borderBottom: "1px solid var(--line, #eee)" }}>
                                    {moduleHeading && (
                                        <h3 style={{ flexBasis: "100%", margin: "0.5rem 0 0.1rem",
                                            fontSize: "0.72rem", textTransform: "uppercase",
                                            letterSpacing: "0.06em", color: "var(--muted)" }}>
                                            {moduleHeading}
                                        </h3>
                                    )}
                                    <span style={{ minWidth: "16rem", fontWeight: 600 }}>{p.label}</span>
                                    <span className={`chip ${allowed ? "pass" : "mute"}`}>
                                        {allowed ? "Allowed" : "Not allowed"}
                                    </span>
                                    {p.enforced === false && (
                                        <span className="chip mute"
                                            title="This permission documents today's role rule. Its screens still check the role directly, so an override here takes effect when the module adopts the engine.">
                                            not yet enforced
                                        </span>
                                    )}
                                    <span style={{ fontSize: "0.75rem", color: "var(--muted)", flex: 1 }}>
                                        {state.role === "admin"
                                            ? "the admin is always allowed"
                                            : override
                                                ? `override (${override.effect}): ${override.reason}`
                                                  + (override.endsAt ? ` — until ${formatDay(override.endsAt)}` : "")
                                                  + (override.grantedByName ? ` — by ${override.grantedByName}` : "")
                                                : byDefault ? `allowed by the ${state.role} role` : `not in the ${state.role} role`}
                                    </span>
                                    {state.role !== "admin" && (
                                        <span style={{ display: "flex", gap: "0.3rem" }}>
                                            {override ? (
                                                <button className="btn ghost small" disabled={busy}
                                                    onClick={() => void clear(p.key)}>
                                                    Remove override
                                                </button>
                                            ) : (
                                                <>
                                                    {!byDefault && (
                                                        <button className="btn ghost small" disabled={busy}
                                                            onClick={() => setEditor({ key: p.key, effect: "grant", reason: "", endsAt: "" })}>
                                                            Grant
                                                        </button>
                                                    )}
                                                    {byDefault && !p.floor && (
                                                        <button className="btn ghost small" disabled={busy}
                                                            onClick={() => setEditor({ key: p.key, effect: "deny", reason: "", endsAt: "" })}>
                                                            Deny
                                                        </button>
                                                    )}
                                                    {byDefault && p.floor && (
                                                        <span className="chip mute" title="Reporting is never blocked — this permission cannot be denied.">
                                                            floor
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </span>
                                    )}
                                    {editor?.key === p.key && (
                                        <div style={{ flexBasis: "100%", display: "flex", gap: "0.4rem",
                                            flexWrap: "wrap", alignItems: "center", padding: "0.3rem 0 0.1rem" }}>
                                            <span className="chip block">{editor.effect === "grant" ? "Granting" : "Denying"}</span>
                                            <input style={{ flex: 1, minWidth: "14rem" }} value={editor.reason}
                                                placeholder="Why — this is the audit record"
                                                onChange={(e) => setEditor({ ...editor, reason: e.target.value })} />
                                            <label style={{ fontSize: "0.75rem", color: "var(--muted)" }}>
                                                Until{" "}
                                                <input type="date" value={editor.endsAt}
                                                    onChange={(e) => setEditor({ ...editor, endsAt: e.target.value })} />
                                            </label>
                                            <button className="btn small" disabled={busy || editor.reason.trim() === ""}
                                                onClick={() => void save()}>
                                                Save
                                            </button>
                                            <button className="btn ghost small" disabled={busy} onClick={() => setEditor(null)}>
                                                Cancel
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {message && <span style={{ fontSize: "0.8rem", color: "var(--muted)" }}>{message}</span>}
                    </div>
                )}
            </div>
        </div>
    );
};
